import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { RootState } from '@redux/store';
import zenhammerLogo from '@assets/logo.png';
import BusinessIcon from '@mui/icons-material/Business';
import TrialCountdownPill from '@components/atoms/TrialCountdownPill';
import {
  useTrialStatus,
  TRIAL_URGENT_THRESHOLD_DAYS,
} from '@hooks/useTrialStatus';
import { useFeatureAccess } from '@utils/featureFlag';
import { useTranslation } from '@hooks/useTranslation';

export const SidebarHeader = () => {
  const tCommon = useTranslation('common');
  const organizationName = useSelector(
    (state: RootState) => state.organization.name,
  );
  const userRole = useSelector((state: RootState) => state.user.role);
  const { isOnTrial, isBillingLocked, daysRemaining, isEndingSoon } =
    useTrialStatus();
  const history = useHistory();
  const { canAccess } = useFeatureAccess();
  const canOpenBilling = canAccess('BILLING');

  const formatRole = (role: string | null) => {
    if (!role) return '';
    return role
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // The countdown lives here rather than in a banner because it needs to be persistently
  // visible without competing for attention — it appears only in the final week, when it
  // is actually actionable.
  const showCountdown = isOnTrial && isEndingSoon && daysRemaining !== null;

  return (
    <div className="px-3.5 pt-3.5 pb-2">
      <div className="flex w-full flex-col items-center justify-center">
        <img src={zenhammerLogo} alt="zenhammer" className="h-7" />
      </div>
      {organizationName && (
        <div className="mt-3 w-full bg-indigo-700 rounded-lg p-2 flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">
                {organizationName}
              </p>
              {userRole && (
                <p className="text-xs text-indigo-200 mt-1 truncate">
                  {formatRole(userRole)}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <BusinessIcon fontSize="small" className="text-white" />
            </div>
          </div>

          {showCountdown && (
            <TrialCountdownPill
              daysRemaining={daysRemaining}
              urgent={daysRemaining <= TRIAL_URGENT_THRESHOLD_DAYS}
              className="self-start"
            />
          )}

          {isBillingLocked &&
            (canOpenBilling ? (
              <button
                type="button"
                onClick={() => history.push('/billing')}
                title={tCommon.goToBillingAddPaymentMethod}
                className="inline-flex self-start items-center gap-0.5 whitespace-nowrap rounded-full bg-amber-400 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-amber-950 transition-colors hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-200">
                {tCommon.trialEndedReadOnly}
                <ChevronRight className="h-3 w-3" />
              </button>
            ) : (
              // Only an OWNER can act on this: /billing is OWNER-gated and every
              // mutation that lifts the paywall is OWNER-only. For everyone else the
              // badge stays a statement of fact rather than a link to a dead end.
              <span className="inline-flex self-start items-center whitespace-nowrap rounded-full bg-amber-400 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-amber-950">
                {tCommon.trialEndedReadOnly}
              </span>
            ))}
        </div>
      )}
    </div>
  );
};
